import SectionIcon from '@components/SectionIcon';
import { Flex, Text } from '@retracedgmbh/ui-components-lib';
import useTranslator from '@/hooks/useTranslator';
import QueueCard from './QueueCard';
import type { AiProcessingItem } from './types';

type SectionVariant = 'inProgress' | 'failed' | 'review';

interface QueueSectionProps {
  variant: SectionVariant;
  items: AiProcessingItem[];
  onCta: (item: AiProcessingItem) => void;
}

const SECTION_ICON: Record<
  SectionVariant,
  {
    icon: string;
    iconClassName?: string;
    type: 'primary' | 'destructive' | 'warning';
  }
> = {
  inProgress: {
    icon: 'fa-light fa-circle-notch',
    iconClassName: 'tw:animate-spin',
    type: 'primary',
  },
  failed: { icon: 'fa-regular fa-triangle-exclamation', type: 'destructive' },
  review: { icon: 'fa-regular fa-circle-check', type: 'warning' },
};

const SECTION_TITLE_KEYS: Record<SectionVariant, string> = {
  inProgress: 'section.inProgress',
  failed: 'section.failed',
  review: 'section.readyToReview',
};

export default function QueueSection({
  variant,
  items,
  onCta,
}: QueueSectionProps) {
  const { getTranslation } = useTranslator({ page: 'ai-processing' });

  if (items.length === 0) return null;

  const icon = SECTION_ICON[variant];

  return (
    <Flex direction='column' gap='md'>
      <Flex alignItems='center' gap='md'>
        <SectionIcon
          icon={icon.icon}
          iconClassName={icon.iconClassName}
          type={icon.type}
          size='md'
        />
        <Text size='base' weight='semibold'>
          {getTranslation(SECTION_TITLE_KEYS[variant], { count: items.length })}
        </Text>
      </Flex>

      <Flex direction='column' gap='sm'>
        {items.map((item, index) => (
          <QueueCard
            key={item.id}
            item={item}
            index={index + 1}
            section={variant}
            onCta={onCta}
          />
        ))}
      </Flex>
    </Flex>
  );
}
